import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useSearchParams } from 'react-router'
import { api, query } from '../api/client'
import type { AppDetail, AppSummary, RankSeries } from '../api/types'
import { RankChart } from '../components/app/RankChart'
import { ChartPickers } from '../components/charts/ChartPickers'
import { AppPicker } from '../components/compare/AppPicker'
import { CompareRatings } from '../components/compare/CompareRatings'
import { RangePicker, rangeFrom, type RangeKey } from '../components/RangePicker'
import { Segmented } from '../components/Segmented'
import { useChartSelection } from '../hooks/useChartSelection'
import { SERIES_COLORS, seriesLabel } from '../lib/rank-series'

type Mode = 'chart' | 'best'
type View = 'ranks' | 'ratings'

export function CompareScreen() {
	const [selection, setSelection] = useChartSelection()
	const [params, setParams] = useSearchParams()
	const ids = (params.get('apps') ?? '').split(',').map(Number).filter(Boolean)
	const mode = (params.get('mode') as Mode) ?? 'chart'
	const view = (params.get('view') as View) ?? 'ranks'
	const range = (params.get('r') as RangeKey) ?? '30d'
	// Fixed per range: `rangeFrom` is clock-based, and a new value each render would re-key the ratings queries forever.
	const from = useMemo(() => rangeFrom(range), [range])
	const set = (key: string, value: string) => {
		const next = new URLSearchParams(params)
		next.set(key, value)
		setParams(next, { replace: true })
	}

	const details = useQueries({
		queries: ids.map((id) => ({ queryKey: ['app', id], queryFn: () => api.get<AppDetail>(`/api/v1/apps/${id}`) })),
	})
	const apps: AppSummary[] = ids.map(
		(id, i) => details[i].data ?? { id, name: `App ${id}`, developerName: null, iconUrl: null },
	)
	const filter =
		mode === 'chart'
			? { platform: selection.platform, storefront: selection.storefront, genre: selection.genre, type: selection.type }
			: {}
	const ranks = useQueries({
		queries: ids.map((id) => ({
			queryKey: ['ranks', id, mode, filter, range],
			queryFn: () => api.get<RankSeries[]>(`/api/v1/apps/${id}/ranks${query({ ...filter, from, charts: 1 })}`),
		})),
	})
	// One series per app; give each a unique id so apps sharing a chart don't collide.
	const series = ranks.flatMap((r, i) =>
		r.data?.[0] ? [{ app: apps[i], index: i, s: { ...r.data[0], chart: { ...r.data[0].chart, id: i + 1 } } }] : [],
	)
	const missing = ranks.map((r, i) => (r.data && r.data.length === 0 ? apps[i].name : null)).filter(Boolean)

	return (
		<div className="stack">
			<div className="page-header">
				<h1>Compare</h1>
				<div className="row">
					<Segmented
						label="View"
						value={view}
						options={[
							{ value: 'ranks', label: 'Ranks' },
							{ value: 'ratings', label: 'Ratings' },
						]}
						onChange={(v) => set('view', v)}
					/>
					<RangePicker value={range} onChange={(r) => set('r', r)} />
				</div>
			</div>
			<AppPicker
				apps={apps}
				onAdd={(a) => set('apps', [...ids, a.id].join(','))}
				onRemove={(id) => set('apps', ids.filter((x) => x !== id).join(','))}
			/>
			<div className="row">
				{view === 'ranks' && (
					<Segmented
						label="Chart mode"
						value={mode}
						options={[
							{ value: 'chart', label: 'One chart' },
							{ value: 'best', label: 'Each app’s best chart' },
						]}
						onChange={(m) => set('mode', m)}
					/>
				)}
				{(mode === 'chart' || view === 'ratings') && (
					<ChartPickers
						value={selection}
						onChange={setSelection}
						showGenre={view === 'ranks'}
						showType={view === 'ranks'}
					/>
				)}
			</div>
			<section className="card">
				{ids.length < 2 && <div className="empty">Add at least two apps to compare.</div>}
				{ids.length >= 2 && view === 'ratings' && (
					<CompareRatings apps={apps} storefront={selection.storefront} from={from} />
				)}
				{ids.length >= 2 && view === 'ranks' && (
					<>
						<RankChart
							series={series.map((x) => x.s)}
							colorFor={(s) => SERIES_COLORS[(s.chart.id - 1) % SERIES_COLORS.length]}
							labelFor={(s) => {
								const x = series.find((y) => y.s === s)!
								return mode === 'best' ? `${x.app.name} (${seriesLabel(s)})` : x.app.name
							}}
						/>
						{mode === 'best' && (
							<ul className="small muted" style={{ margin: '0.5rem 0 0', paddingLeft: '1.2rem' }}>
								{series.map((x) => (
									<li key={x.app.id}>
										<span style={{ color: SERIES_COLORS[x.index % SERIES_COLORS.length] }}>●</span> {x.app.name}:{' '}
										{seriesLabel(x.s)} · best #{x.s.bestRank}
									</li>
								))}
							</ul>
						)}
						{missing.length > 0 && (
							<p className="muted small">
								Not ranked in this {mode === 'chart' ? 'chart' : 'period'}: {missing.join(', ')}
							</p>
						)}
					</>
				)}
			</section>
		</div>
	)
}
